import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetBenefitDetail } from '@/common/integrations/menhir/employee/benefits/benefits.hooks';
import { useGetDiscounts } from '@/common/integrations/menhir/employee/discounts/discounts.hooks';
import { useGetPockets } from '@/common/integrations/menhir/employee/pockets/pockets.hooks';
import { getBenefitExemptionsSlugs } from '@/common/utils/benefits/benefit-utils';
import { DiscountsList } from '@/web/components/DiscountsList/DiscountsList';
import { FormattedAmount } from '@/web/components/FormattedAmount/FormattedAmount';
import { InfoButton } from '@/web/components/InfoButton/InfoButton';
import { LoadingPlaceholder } from '@/web/components/LoadingPlaceholder/LoadingPlaceholder';
import { MovementsList } from '@/web/components/TransactionList/components/MovementsList/MovementsList';
import { useMovements } from '@/web/hooks/useMovements';
import { usePageViewTracking } from '@/web/hooks/usePageViewTracking';
import { PageLayout } from '@/web/layouts/PageLayout/PageLayout';
import { Flex, Heading, Text } from '@coverflex-tech/hypernova';
import { MealLearningCentre } from './components/MealLearningCentre/MealLearningCenter';
import { css } from './MealCardPage.css';

export const MealCardPage = () => {
  const { t } = useTranslation();
  usePageViewTracking('meal_page');
  const { mealPocket } = useGetPockets();
  const { mealsDiscounts } = useGetDiscounts();
  const [displayLearningCentre, toggleLearningCentre] = useState(false);

  const { movements, isLoading: isLoadingMovements } = useMovements({
    pocketType: 'meals',
  });

  const { benefit } = useGetBenefitDetail({ slug: 'meals' });

  if (!benefit) {
    return <LoadingPlaceholder />;
  }

  const exemptions = getBenefitExemptionsSlugs(benefit);

  return (
    <PageLayout>
      {displayLearningCentre && (
        <MealLearningCentre onClose={() => toggleLearningCentre(false)} />
      )}
      <Flex gap="xs" flexDirection="column">
        <Heading level={1} levelSize={4}>
          {t('dashboard.meal_card.title')}
        </Heading>
        <Flex alignItems="flex-end" justifyContent="space-between">
          <FormattedAmount
            className={css.balance}
            amount={mealPocket?.balance}
          />
          <InfoButton onClick={() => toggleLearningCentre(true)} />
        </Flex>
        <Flex gap="lg">
          {exemptions.map((exemption, idx) => (
            <Text size="sm" className={css.advantage} key={idx}>
              <span>{t(`dashboard.benefits.exemptions.${exemption}`)}</span>
            </Text>
          ))}
        </Flex>
      </Flex>
      {mealsDiscounts && mealsDiscounts.length > 0 && (
        <DiscountsList
          discounts={mealsDiscounts.slice(0, 2)}
          context="meal_page"
        />
      )}
      <MovementsList
        movements={movements?.list ?? []}
        isGrouped
        loading={isLoadingMovements}
      />
    </PageLayout>
  );
};
